import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Button, type ButtonProps, DialogRoot, DialogTrigger, toast } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { apiClient, type SiteCapability, type FeatureUpgradeAction } from '../../../state';
import { AdaptiveContentBuilderDialog } from '../../AdaptiveContent';

export interface SiteItemAdaptiveContentButtonProps extends Pick<ButtonProps, 'size'> {
    item: api.SiteSection | api.SiteSectionGroup | api.SiteSpace | api.SiteExternalLink;
    organization: api.Organization;
    site: api.Site;
    sitePermissions: SiteCapability;
    upgrade?: FeatureUpgradeAction;
}

export function SiteItemAdaptiveContentButton(props: SiteItemAdaptiveContentButtonProps) {
    const { size, item, organization, site, sitePermissions, upgrade } = props;
    const { name, target } = (() => {
        switch (item.object) {
            case 'site-section':
                return { name: 'section', target: 'section' } as const;
            case 'site-section-group':
                return { name: 'section group', target: 'section-group' } as const;
            case 'site-space':
                return { name: 'variant', target: 'variant' } as const;
            case 'site-external-link':
                return { name: 'external link', target: 'external-link' } as const;
            default:
                assertNever(item);
        }
    })();
    const onSaveItemCondition = async (expr: string) => {
        await toast.catch(async () => {
            switch (item.object) {
                case 'site-section': {
                    await apiClient.siteSections.updateSiteSectionById(
                        organization.id,
                        site.id,
                        item.id,
                        {
                            condition: expr.length > 0 ? expr : null,
                        },
                        { returnPreference: 'minimal' }
                    );
                    break;
                }
                case 'site-section-group': {
                    await apiClient.siteSectionGroups.updateSiteSectionGroupById(
                        organization.id,
                        site.id,
                        item.id,
                        {
                            condition: expr.length > 0 ? expr : null,
                        },
                        { returnPreference: 'minimal' }
                    );
                    break;
                }
                case 'site-space': {
                    await apiClient.siteSpaces.updateSiteSpaceById(
                        organization.id,
                        site.id,
                        item.id,
                        {
                            condition: expr.length > 0 ? expr : null,
                        },
                        { returnPreference: 'minimal' }
                    );
                    break;
                }
                case 'site-external-link': {
                    await apiClient.siteExternalLinks.updateSiteExternalLinkById(
                        organization.id,
                        site.id,
                        item.id,
                        {
                            condition: expr.length > 0 ? expr : null,
                        },
                        { returnPreference: 'minimal' }
                    );
                    break;
                }
                default:
                    assertNever(item);
            }
            toast.success(`Condition for ${name} updated successfully!`);
        }, `Error while updating ${name} condition`);
    };
    if (upgrade && sitePermissions.canCreateSite) {
        return <Button {...upgrade} size={size} iconOnly />;
    }
    return (
        <DialogRoot>
            <DialogTrigger>
                <Button
                    kind="blank"
                    iconOnly
                    size={size}
                    leadingIcon={Icon.Condition}
                    label={`This ${name} is conditionally shown to visitors. ${sitePermissions.canCreateSite ? 'Click to edit.' : 'Only site creators and administrators can edit this.'}`}
                    disabled={!sitePermissions.canCreateSite}
                />
            </DialogTrigger>

            <AdaptiveContentBuilderDialog
                initialTab="expression"
                organizationId={organization.id}
                siteId={site.id}
                tabs={[
                    {
                        tab: 'expression',
                        target,
                        expression: item.condition || undefined,
                        onSaveExpression: onSaveItemCondition,
                    },
                    {
                        tab: 'schema',
                    },
                ]}
            />
        </DialogRoot>
    );
}
